<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2 pt-4'">
    <!-- Desktop View -->
    <v-data-table
      v-if="!device.isMobile"
      :headers="headers"
      :items="orders"
      class="striped-table ordera-box-shadow"
      :search="search"
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
      :loading="loading"
    >
      <template v-slot:no-data>
        <empty-state
          icon="mdi-package-variant-closed"
          :title="$t('no_orders')"
          :subtitle="$t('no_orders_for_selected_date')"
        />
      </template>

      <template v-slot:item.num="{ item }">
        {{ item.num }}
      </template>

      <template v-slot:item.user_name="{ item }">
        <div class="font-weight-medium">{{ item.user_name }}</div>
      </template>

      <template v-slot:item.restaurant="{ item }">
        <div class="d-flex align-center">
          <v-icon small class="mr-1" color="orange">mdi-store</v-icon>
          {{ item.restaurant }}
        </div>
      </template>

      <template v-slot:item.meals="{ item }">
        <div v-for="(meal, index) in item.meals" :key="index" class="mb-1">
          <span class="font-weight-medium">{{ meal.quantity }}x</span> {{ meal.name }}
        </div>
      </template>

      <template v-slot:item.delivery_location="{ item }">
        <div class="d-flex align-center">
          <v-icon small class="mr-1" color="orange">mdi-map-marker</v-icon>
          {{ item.delivery_location }}
        </div>
      </template>

      <template v-slot:item.action="{ item }">
        <circle-button
          icon="mdi-eye"
          @click="viewOrderDetails(item)"
          color="primary"
          :tooltip="$t('view_details')"
        />
      </template>
    </v-data-table>

    <!-- Mobile View -->
    <mobile-data-cards
      v-else
      :headers="mobileHeaders"
      :items="orders"
      :loading="loading"
      item-key="id"
      :fields-per-row="2"
      :bold-fields="['user_name']"
      :show-row-number="false"
      class="px-3"
      @click:item="viewOrderDetails"
    >
      <template v-slot:item.user_name="{ item }">
        <div>{{ item.user_name }}</div>
        <div class="text-caption grey--text">{{ item.user_id }}</div>
      </template>

      <template v-slot:item.restaurant="{ item }">
        <v-chip x-small color="primary" outlined>
          {{ item.restaurant }}
        </v-chip>
      </template>

      <template v-slot:item.meals="{ item }">
        <div v-for="(meal, index) in item.meals" :key="index" class="text-caption">
          {{ meal.quantity }}x {{ meal.name }}
        </div>
      </template>

      <template v-slot:item.delivery_location="{ item }">
        <v-icon x-small class="mr-1" color="orange">mdi-map-marker</v-icon>
        {{ item.delivery_location }}
      </template>
    </mobile-data-cards>

    <!-- Order Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="600">
      <v-card v-if="selectedOrder">
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-package-variant</v-icon>
          {{ $t("order_details") }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">{{ $t("order_id") }}</div>
              <div class="font-weight-bold">#{{ selectedOrder.id }}</div>
            </v-col>
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">{{ $t("user") }}</div>
              <div class="font-weight-bold">{{ selectedOrder.user_name }}</div>
              <div class="text-caption">ID: {{ selectedOrder.user_id }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">{{ $t("restaurant") }}</div>
              <v-chip small color="primary" outlined>
                {{ selectedOrder.restaurant }}
              </v-chip>
            </v-col>
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
            <v-col cols="12">
              <div class="text-caption grey--text mb-2">{{ $t("meals") }}</div>
              <v-list dense>
                <v-list-item v-for="(meal, index) in selectedOrder.meals" :key="index">
                  <v-list-item-content>
                    <v-list-item-title>
                      <span class="font-weight-bold">{{ meal.quantity }}x</span>
                      {{ meal.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="meal.shift">
                      {{ $t("shift") }}: {{ meal.shift }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    {{ formatCurrency(meal.price) }}
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
            <v-col cols="12">
              <div class="text-caption grey--text">{{ $t("delivery_location") }}</div>
              <div class="d-flex align-center">
                <v-icon color="orange" class="mr-2">mdi-map-marker</v-icon>
                <span class="font-weight-bold">{{
                  selectedOrder.delivery_location
                }}</span>
              </div>
              <div class="text-caption mt-1" v-if="selectedOrder.delivery_notes">
                {{ selectedOrder.delivery_notes }}
              </div>
            </v-col>
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="detailsDialog = false">
            {{ $t("close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "CompanyMultiRestaurantTabPackages",
  mixins: [hasSnackbar, hasPrinting, hasReportPrinting, hasExcel],
  components: {
    EmptyState,
    MobileDataCards,
  },
  data() {
    return {
      loading: false,
      detailsDialog: false,
      selectedOrder: null,
      // Mock data - TODO: Replace with API call
      orders: [
        {
          id: 1001,
          num: 1,
          user_id: "U12345",
          user_name: "Marko Marković",
          restaurant: "Restoran Kod Marka",
          order_time: "2026-01-11T08:30:00",
          meals: [
            { name: "Piletina sa povrćem", quantity: 2, shift: 1, price: 12.5 },
            { name: "Čorba", quantity: 2, shift: 1, price: 3.0 },
          ],
          delivery_location: "Kancelarija A - Sprat 3",
          delivery_notes: "Ostaviti na recepciji",
        },
        {
          id: 1002,
          num: 2,
          user_id: "U12346",
          user_name: "Ana Anić",
          restaurant: "Pizzeria Napoli",
          order_time: "2026-01-11T09:15:00",
          meals: [{ name: "Pizza Margherita", quantity: 1, shift: 2, price: 15.0 }],
          delivery_location: "Kancelarija B - Sprat 2",
          delivery_notes: null,
        },
        {
          id: 1003,
          num: 3,
          user_id: "U12347",
          user_name: "Petar Petrović",
          restaurant: "Restoran Kod Marka",
          order_time: "2026-01-11T08:45:00",
          meals: [
            { name: "Ćevapi sa kajmakom", quantity: 1, shift: 1, price: 10.0 },
            { name: "Salata", quantity: 1, shift: 1, price: 4.0 },
          ],
          delivery_location: "Kancelarija C - Sprat 1",
          delivery_notes: null,
        },
        {
          id: 1004,
          num: 4,
          user_id: "U12348",
          user_name: "Jovana Jovanović",
          restaurant: "Sushi Bar Tokyo",
          order_time: "2026-01-11T10:00:00",
          meals: [
            { name: "California Roll", quantity: 2, shift: 2, price: 18.0 },
            { name: "Miso Supa", quantity: 1, shift: 2, price: 5.0 },
          ],
          delivery_location: "Kancelarija D - Sprat 4",
          delivery_notes: "Bez wasabija",
        },
        {
          id: 1005,
          num: 5,
          user_id: "U12349",
          user_name: "Milan Milić",
          restaurant: "Pizzeria Napoli",
          order_time: "2026-01-11T09:30:00",
          meals: [{ name: "Pasta Carbonara", quantity: 1, shift: 2, price: 13.0 }],
          delivery_location: "Kancelarija A - Sprat 3",
          delivery_notes: null,
        },
        {
          id: 1006,
          num: 6,
          user_id: "U12350",
          user_name: "Sara Sarić",
          restaurant: "Restoran Kod Marka",
          order_time: "2026-01-11T08:20:00",
          meals: [
            { name: "Grah", quantity: 1, shift: 1, price: 8.0 },
            { name: "Hleb", quantity: 1, shift: 1, price: 1.5 },
          ],
          delivery_location: "Kancelarija E - Sprat 2",
          delivery_notes: null,
        },
      ],
    };
  },
  computed: {
    companyId() {
      return this.$store.state.company?.id;
    },
    restaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    selectedRestaurants() {
      return this.$store.state.report.selectedRestaurants || [];
    },
    selectedDate() {
      const { from } = this.$store.state.report.filters.dates;
      return from;
    },
    search() {
      return this.$store.state.report.filters.search || "";
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("fullname"), value: "user_name" },
        { text: this.$t("restaurant"), value: "restaurant" },
        { text: this.$t("meals"), value: "meals", sortable: false },
        { text: this.$t("delivery_location"), value: "delivery_location" },
        {
          text: this.$t("actions"),
          value: "action",
          sortable: false,
          align: "center",
        },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("fullname"), value: "user_name" },
        { text: this.$t("restaurant"), value: "restaurant" },
        { text: this.$t("meals"), value: "meals" },
        { text: this.$t("delivery_location"), value: "delivery_location" },
      ];
    },
  },
  methods: {
    formatCurrency(amount) {
      return (parseFloat(amount) || 0).toFixed(2) + " KM";
    },
    formatOrdersForExport() {
      return this.orders.map((item, index) => ({
        num: index + 1,
        user_name: item.user_name,
        restaurant: item.restaurant,
        meals: item.meals.map((m) => `${m.quantity}x ${m.name}`).join(", "),
        delivery_location: item.delivery_location,
      }));
    },
    viewOrderDetails(order) {
      this.selectedOrder = order;
      this.detailsDialog = true;
    },
  },
  watch: {
    selectedDate: {
      handler() {
        // TODO: Fetch orders for selected date
      },
      immediate: true,
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("packages")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("packages"),
            headers: this.headers.filter((h) => h.value !== "action"),
            items: this.formatOrdersForExport(),
            title: [this.$t("report"), this.$t("packages")].join(" "),
            subtitle: this.subtitle,
          },
        ],
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;
      const restaurantNames = this.selectedRestaurants
        .map((id) => this.restaurants.find((r) => r.id === id)?.name)
        .filter(Boolean)
        .map(this.toLatin)
        .join(", ");

      this.printDocument({
        tables: [
          {
            headers: this.headers.filter((h) => h.value !== "action"),
            items: this.formatOrdersForExport(),
            showCaption: true,
          },
        ],
        title: [this.$t("report"), this.$t("packages")].join(": "),
        subtitle: this.subtitle,
        afterTableHtml: `<div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd;">
          <strong>${this.$t("restaurants")}:</strong> ${restaurantNames}
        </div>`,
        hideDatetime: true,
      });
    },
  },
};
</script>
